// Source: commanddeck-hq @ be1f879 · src/components/layout/AppSidebar.tsx
// Modified for Reverra: nav grown from 9 → 10 items in W5 Phase 3
// (Commissions added; previously deferred to W5-6 — now live).
import { useLocation, Link } from 'react-router-dom';
import {
  LayoutDashboard,
  Mail,
  MessageSquare,
  Calendar,
  Shield,
  Bot,
  Lightbulb,
  DollarSign,
  Settings,
  Lock,
  Activity,
  FileText,
  CheckSquare,
  Plug,
  FlaskConical,
  LogOut,
} from 'lucide-react';
import SystemStatusChip from '@/components/layout/SystemStatusChip';
import { useAuth } from '@/lib/auth';
import { useOwnerProfile } from '@/hooks/useOwnerProfile';

const navItems = [
  { icon: LayoutDashboard, label: 'Overview', path: '/' },
  { icon: Mail, label: 'Inbox', path: '/inbox' },
  { icon: MessageSquare, label: 'Messages', path: '/messages' },
  { icon: Calendar, label: 'Meetings', path: '/meetings' },
  { icon: FileText, label: 'Agreements', path: '/agreements' },
  { icon: CheckSquare, label: 'Action Items', path: '/action-items' },
  { icon: Shield, label: 'Focus Time', path: '/focus' },
  { icon: DollarSign, label: 'Commissions', path: '/commissions' },
  { icon: Bot, label: 'Agents', path: '/agents' },
  { icon: Lightbulb, label: 'Intel', path: '/intel' },
  { icon: Plug, label: 'Connections', path: '/connections' },
  { icon: FlaskConical, label: 'Testing', path: '/testing' },
  { icon: Settings, label: 'Settings', path: '/settings' },
];

const AppSidebar = () => {
  const location = useLocation();
  const { user, signOut } = useAuth();
  // Operator identity from the owner's profile — not hardcoded — so the
  // dashboard shows the actual client on each deploy.
  const { name: ownerName, title: ownerTitle, initials: ownerInitials } = useOwnerProfile();

  return (
    <aside
      className="fixed left-0 top-0 h-screen z-30 w-[240px] bg-card flex flex-col"
      style={{ borderRight: '1px solid var(--border-soft)' }}
    >
      {/* Brand */}
      <div
        className="h-[72px] flex items-center px-5"
        style={{ borderBottom: '1px solid var(--border-soft)' }}
      >
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #1F4437 0%, #2D7D5F 100%)' }}
        >
          <Activity className="w-6 h-6 text-primary-foreground" />
        </div>
        <div className="ml-3">
          <span className="text-lg font-semibold text-foreground tracking-tight">Reverra</span>
          <span className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground ml-1.5">
            AI
          </span>
        </div>
      </div>

      {/* Profile */}
      <div className="px-5 py-4" style={{ borderBottom: '1px solid var(--border-soft)' }}>
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-primary-foreground"
            style={{ background: 'linear-gradient(135deg, #1F4437 0%, #2D7D5F 100%)' }}
          >
            {ownerInitials}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground truncate" title={ownerName}>
              {ownerName}
            </p>
            {ownerTitle && <p className="text-xs text-muted-foreground truncate">{ownerTitle}</p>}
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 py-3 flex flex-col gap-0.5 px-3">
        {navItems.map((item) => {
          // Testing hosts Tickets + Board as in-page tabs; keep it highlighted
          // for the old /tickets and /board deep links too.
          const active =
            location.pathname === item.path ||
            (item.path === '/testing' &&
              (location.pathname === '/tickets' || location.pathname === '/board'));
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                active
                  ? 'neo-chip text-forest font-medium'
                  : 'text-foreground/70 hover:bg-secondary hover:text-foreground'
              }`}
            >
              <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
              <span className="text-[13px]">{item.label}</span>
            </Link>
          );
        })}

        {/* Partner Instances - locked */}
        <div
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-muted-foreground/30 cursor-not-allowed"
          title="Coming soon"
        >
          <Lock className="w-[18px] h-[18px] flex-shrink-0" />
          <span className="text-[13px]">Partner Instances</span>
        </div>
      </nav>

      {/* Bottom status + identity. Email + sign-out previously lived on
          every page's top-right header (duplicated 7×, visually noisy);
          consolidated here so each page's header can lean on the title
          + subtitle alone. Sign-out is an icon-only button with a
          native title tooltip on hover — no popover library needed. */}
      <div className="px-4 py-3 space-y-2" style={{ borderTop: '1px solid var(--border-soft)' }}>
        <SystemStatusChip />
        {user && (
          <div className="flex items-center gap-2 px-3 py-2">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground/70 leading-none mb-0.5">
                Signed in as
              </p>
              <p
                className="text-[13px] font-medium text-foreground truncate"
                title={user.email ?? undefined}
              >
                {user.email}
              </p>
            </div>
            <button
              type="button"
              onClick={() => signOut()}
              title="Sign out"
              aria-label="Sign out"
              className="p-1.5 rounded-md text-muted-foreground hover:text-alert hover:bg-alert/5 transition-colors flex-shrink-0"
            >
              <LogOut className="w-[18px] h-[18px]" />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
};

export default AppSidebar;
